// API for Terimaan data (consolidated)
import { API_BASE_URL } from "./base";
import { withCsrfHeaders, fetchWithCsrfRetry } from "./csrf";

const headersWithDept = (dept) => ({
  "Content-Type": "application/json",
  "X-User-Department": dept,
});

const getDefaultDept = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return user.bahagian;
};

// Get terimaan by tarikh_resit range only
export const apiGetTerimaanByDateRange = async (tarikh_mula, tarikh_tamat, bahagianOverride) => {
  // If user chooses SEMUA we pass bahagian=SEMUA; backend expands to all (excluding admin)
  const dept = getDefaultDept();
  if (!dept) throw new Error("bahagian_pemantau diperlukan");
  const params = new URLSearchParams();
  params.set('tarikh_mula', tarikh_mula);
  params.set('tarikh_tamat', tarikh_tamat);
  if (bahagianOverride && bahagianOverride.trim() !== '') {
    params.set('bahagian', bahagianOverride);
  }
  const res = await fetch(`${API_BASE_URL}/terimaan/by-date-range?${params.toString()}` , {
    method: "GET",
    headers: headersWithDept(dept),
  });
  if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
  return res.json();
};

export const apiGetBahagianList = async () => {
  const res = await fetch(`${API_BASE_URL}/pengguna/bahagian`, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
  if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
  const json = await res.json();
  const list = Array.isArray(json.bahagian) ? json.bahagian : [];
  // Ensure SEMUA appears first
  return ['SEMUA', ...list];
};

export const apiGetTerimaanByNoAkaun = async (noAkaun) => {
  const dept = getDefaultDept();
  if (!dept) throw new Error("bahagian_pemantau diperlukan");
  const res = await fetch(`${API_BASE_URL}/terimaan/noakaun/${encodeURIComponent(noAkaun)}`, {
    method: "GET",
    headers: headersWithDept(dept),
    credentials: "include",
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
  return res.json();
};

export const apiCreateTerimaan = async (terimaanData, deptOverride) => {
  const dept = deptOverride || getDefaultDept();
  if (!dept) throw new Error("bahagian_pemantau diperlukan");
  const res = await fetchWithCsrfRetry(`${API_BASE_URL}/terimaan`, {
    method: "POST",
    headers: await withCsrfHeaders(headersWithDept(dept)),
    body: JSON.stringify(terimaanData),
  });
  if (!res.ok) {
    let message = `HTTP error! status: ${res.status}`;
    try {
      const data = await res.json();
      if (data && (data.error || data.message)) {
        message = data.error || data.message;
      }
      // If backend signalled duplicate, enrich message
      if (res.status === 409 && data && (data.code === 'DUPLICATE' || /wujud/i.test(data.error || ''))) {
        message = data.error || 'Rekod terimaan telah wujud';
      }
    } catch (_) {
      // ignore JSON parse errors; keep default message
    }
    const err = new Error(message);
    err.status = res.status;
    throw err;
  }
  return res.json();
};

// Create multiple terimaan/lejar and update inbois in one transaction
export const apiCreateTerimaanBatch = async (payload, deptOverride) => {
  const dept = deptOverride || getDefaultDept();
  if (!dept) throw new Error("bahagian_pemantau diperlukan");
  const res = await fetchWithCsrfRetry(`${API_BASE_URL}/terimaan/batch`, {
    method: 'POST',
    headers: await withCsrfHeaders(headersWithDept(dept)),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let message = `HTTP error! status: ${res.status}`;
    try {
      const data = await res.json();
      if (data && (data.error || data.message)) message = data.error || data.message;
    } catch (_) {}
    const err = new Error(message);
    err.status = res.status;
    throw err;
  }
  return res.json();
};

export const getTerimaanByNoAkaunForDept = async (noAkaun, dept) => {
  if (!dept) throw new Error("bahagian_pemantau diperlukan");
  const res = await fetch(`${API_BASE_URL}/terimaan/noakaun/${encodeURIComponent(noAkaun)}`, {
    method: "GET",
    headers: headersWithDept(dept),
    credentials: "include",
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`Terimaan fetch failed: ${res.status}`);
  return res.json();
};

export const getLejarByNoAkaunForDept = async (noAkaun, dept) => {
  if (!dept) throw new Error("bahagian_pemantau diperlukan");
  const res = await fetch(`${API_BASE_URL}/lejar/noakaun/${encodeURIComponent(noAkaun)}`, {
    method: "GET",
    headers: headersWithDept(dept),
    credentials: "include",
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`Lejar fetch failed: ${res.status}`);
  return res.json();
};

export const getDepositByNoAkaunForDept = async (noAkaun, dept) => {
  if (!dept) throw new Error("bahagian_pemantau diperlukan");
  const res = await fetch(`${API_BASE_URL}/deposit/noakaun/${encodeURIComponent(noAkaun)}`, {
    method: "GET",
    headers: headersWithDept(dept),
    credentials: "include",
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`Deposit fetch failed: ${res.status}`);
  return res.json();
};

// NEW: Cross-department fetches by no_akaun (no department header required)
export const getTerimaanByNoAkaunAll = async (noAkaun) => {
  const res = await fetch(`${API_BASE_URL}/terimaan/noakaun-all/${encodeURIComponent(noAkaun)}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`Terimaan (all) fetch failed: ${res.status}`);
  return res.json();
};

// Special huda-only (no department filter) endpoint.
export const getTerimaanByNoAkaunHuda = async (noAkaun) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const idPengguna = (user.id_pengguna || user.idPengguna || user.username || '').toLowerCase();
  const res = await fetch(`${API_BASE_URL}/terimaan/noakaun-huda/${encodeURIComponent(noAkaun)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', 'X-User-Id': idPengguna },
    credentials: 'include',
    cache: 'no-store'
  });
  if (!res.ok) throw new Error(`Terimaan (huda) fetch failed: ${res.status}`);
  return res.json();
};

export const getLejarByNoAkaunAll = async (noAkaun) => {
  const res = await fetch(`${API_BASE_URL}/lejar/noakaun-all/${encodeURIComponent(noAkaun)}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`Lejar (all) fetch failed: ${res.status}`);
  return res.json();
};

export const getDepositByNoAkaunAll = async (noAkaun) => {
  const res = await fetch(`${API_BASE_URL}/deposit/noakaun-all/${encodeURIComponent(noAkaun)}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`Deposit (all) fetch failed: ${res.status}`);
  return res.json();
};

export const apiTerimaanExistsNoResit = async (no_resit) => {
  const dept = getDefaultDept();
  if (!dept) throw new Error("bahagian_pemantau diperlukan");
  const res = await fetch(
    `${API_BASE_URL}/terimaan/exists-no-resit/${encodeURIComponent(no_resit)}`,
    {
      method: "GET",
      headers: headersWithDept(dept),
    }
  );
  if (!res.ok) throw new Error(`Terimaan check failed: ${res.status}`);
  return res.json();
};

// Delete terimaan by array of IDs (cascade deletes matching lejar rows by no_resit)
export const apiDeleteTerimaan = async (ids) => {
  const dept = getDefaultDept();
  if (!dept) throw new Error('bahagian_pemantau diperlukan');
  const res = await fetch(`${API_BASE_URL}/terimaan`, {
    method: 'DELETE',
    headers: await withCsrfHeaders(headersWithDept(dept)),
    body: JSON.stringify({ ids })
  });
  if (!res.ok) throw new Error(`Terimaan delete failed: ${res.status}`);
  return res.json();
};
